import { Link } from 'react-router-dom';
import { 
  ArrowUpRight, Brain, Sparkles, CheckCircle, Clock,
  MessageSquare, Zap, Target, BookOpen, Trophy,
  Lightbulb, Rocket, GraduationCap, Code2
} from 'lucide-react';

const AIPrograms = () => {
  const modules = [
    {
      number: 1,
      title: 'Introduction to AI & Prompt Engineering',
      duration: '30 min',
      lessons: 4,
      description: 'Understand what AI is, how it works, and why prompt engineering matters.',
      topics: ['What is AI?', 'Meet ChatGPT', 'Why Prompts Matter', 'Your First Prompt']
    },
    {
      number: 2,
      title: 'Prompt Engineering Basics',
      duration: '40 min',
      lessons: 4,
      description: 'Master the P.R.O.M.P.T. Framework for writing effective prompts.',
      topics: ['The P.R.O.M.P.T. Framework', 'Being Specific', 'Providing Context', 'Interactive Practice']
    },
    {
      number: 3,
      title: 'Advanced Prompt Frameworks',
      duration: '45 min',
      lessons: 5,
      description: 'Learn professional techniques used by AI experts.',
      topics: ['Role Prompting', 'Context Setting', 'Few-Shot Learning', 'Step-by-Step Prompting', 'Iterative Refinement']
    },
    {
      number: 4,
      title: 'AI for Everyday Productivity',
      duration: '45 min',
      lessons: 5,
      description: 'Apply AI to real-world scenarios in education, career, and business.',
      topics: ['AI for Education', 'Career Applications', 'Business Use Cases', 'Creative Projects', 'Prompt Templates']
    },
    {
      number: 5,
      title: 'Advanced Prompt Engineering',
      duration: '45 min',
      lessons: 5,
      description: 'Master advanced techniques for complex AI interactions.',
      topics: ['AI Personas', 'Output Formatting', 'Prompt Chaining', 'Quality Control', 'Building AI Workflows']
    },
    {
      number: 6,
      title: 'Final Assessment & Certification',
      duration: '10 min',
      lessons: 2,
      description: 'Demonstrate your mastery and earn your certification.',
      topics: ['Comprehensive Quiz', 'AI Master Certification']
    }
  ];

  const badges = [
    { name: 'AI Explorer', requirement: 'Complete Module 1', icon: Lightbulb, color: '#00AEEF' },
    { name: 'Prompt Engineer', requirement: 'Complete Modules 2-3', icon: Code2, color: '#00FFE0' },
    { name: 'Productivity Pro', requirement: 'Complete Module 4', icon: Rocket, color: '#FF6A00' },
    { name: 'AI Master', requirement: 'Pass Final Assessment (70%+)', icon: Trophy, color: '#A855F7' }
  ];

  const features = [
    {
      icon: MessageSquare,
      title: 'AI Prompt Playground',
      description: 'Test your prompts in real-time with live AI responses. See exactly how different prompts produce different results.'
    },
    {
      icon: Target,
      title: 'P.R.O.M.P.T. Builder',
      description: 'Interactive tool to construct perfect prompts using our proven framework. Fill in the blanks and watch your prompt come together.'
    },
    {
      icon: BookOpen,
      title: 'Ready-to-Use Templates',
      description: '6 professional templates for resumes, study guides, business plans, emails, grants, and interview prep.'
    },
    {
      icon: Trophy,
      title: 'Gamification & Badges',
      description: 'Earn badges as you progress through the course. Unlock the AI Master badge by passing the final assessment.'
    }
  ];

  const promptTemplates = [
    { name: 'Resume Builder', description: 'Craft compelling resumes tailored to specific jobs' },
    { name: 'Study Guide Creator', description: 'Generate comprehensive study materials for any topic' },
    { name: 'Business Plan Writer', description: 'Develop professional business plans and pitches' },
    { name: 'Email Composer', description: 'Write professional emails for any situation' },
    { name: 'Grant Proposal Helper', description: 'Create compelling grant applications' },
    { name: 'Interview Prep Coach', description: 'Practice with AI-generated interview questions' }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="py-24 sm:py-32 bg-[#060F22] relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-[#A855F7]/20 via-[#060F22] to-[#060F22]"></div>
        <div className="absolute top-20 right-20 w-96 h-96 bg-[#A855F7]/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-20 w-64 h-64 bg-[#EC4899]/10 rounded-full blur-3xl"></div>
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#A855F7]/20 border border-[#A855F7]/30 rounded-full px-4 py-2 mb-6">
                <Sparkles className="w-4 h-4 text-[#A855F7]" />
                <span className="text-[#A855F7] text-sm font-semibold">New Course Available</span>
              </div>
              
              <h1 className="text-5xl sm:text-6xl font-black tracking-tighter text-white leading-tight mb-6">
                AI Prompt Engineering{' '}
                <span className="bg-gradient-to-r from-[#A855F7] to-[#EC4899] bg-clip-text text-transparent">Bootcamp</span>
              </h1>
              
              <p className="text-lg sm:text-xl text-[#94A3B8] leading-relaxed mb-8">
                Master the art of communicating with AI. Learn professional prompt engineering techniques to unlock the full potential of ChatGPT and other AI tools in just 4 hours.
              </p>
              
              <div className="flex flex-wrap gap-6 mb-8">
                <div className="flex items-center gap-2 text-[#94A3B8]">
                  <Clock className="w-5 h-5 text-[#A855F7]" />
                  <span>4 Hours Total</span>
                </div>
                <div className="flex items-center gap-2 text-[#94A3B8]">
                  <BookOpen className="w-5 h-5 text-[#A855F7]" />
                  <span>6 Modules</span>
                </div>
                <div className="flex items-center gap-2 text-[#94A3B8]">
                  <MessageSquare className="w-5 h-5 text-[#A855F7]" />
                  <span>Live AI Practice</span>
                </div>
                <div className="flex items-center gap-2 text-[#94A3B8]">
                  <GraduationCap className="w-5 h-5 text-[#A855F7]" />
                  <span>Certificate Included</span>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/enrollment"
                  data-testid="ai-program-enroll-btn"
                  className="inline-flex items-center justify-center bg-gradient-to-r from-[#A855F7] to-[#EC4899] text-white font-bold rounded-xl px-8 py-4 shadow-[0_0_30px_rgba(168,85,247,0.4)] hover:shadow-[0_0_40px_rgba(168,85,247,0.6)] hover:-translate-y-1 transition-all duration-300 gap-2"
                >
                  Start Learning Free
                  <ArrowUpRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/student/login"
                  data-testid="ai-program-login-btn"
                  className="inline-flex items-center justify-center bg-transparent border-2 border-[#A855F7] text-[#A855F7] font-bold rounded-xl px-8 py-4 hover:bg-[#A855F7]/10 transition-all duration-300 gap-2"
                >
                  Already Enrolled? Login
                </Link>
              </div>
            </div>
            
            <div className="hidden lg:block">
              <div className="relative">
                <div className="bg-[#0A1F44]/80 backdrop-blur-xl border border-[#A855F7]/30 rounded-2xl p-6 shadow-2xl">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-3 h-3 rounded-full bg-[#FF5F56]"></div>
                    <div className="w-3 h-3 rounded-full bg-[#FFBD2E]"></div>
                    <div className="w-3 h-3 rounded-full bg-[#27C93F]"></div>
                    <span className="ml-2 text-[#94A3B8] text-sm">AI Prompt Playground</span>
                  </div>
                  <div className="space-y-4">
                    <div className="bg-[#060F22] rounded-lg p-4 border border-[#A855F7]/20">
                      <p className="text-[#94A3B8] text-sm mb-2">Your Prompt:</p>
                      <p className="text-white font-mono text-sm">&quot;Act as a career coach. I&apos;m a recent graduate looking for my first tech job. Give me 3 specific tips for standing out in interviews.&quot;</p>
                    </div>
                    <div className="bg-gradient-to-r from-[#A855F7]/10 to-[#EC4899]/10 rounded-lg p-4 border border-[#A855F7]/30">
                      <p className="text-[#A855F7] text-sm mb-2 flex items-center gap-2">
                        <Sparkles className="w-4 h-4" />
                        AI Response:
                      </p>
                      <p className="text-white text-sm leading-relaxed">Great question! Here are 3 tips tailored for new tech graduates:</p>
                      <ul className="text-[#94A3B8] text-sm mt-2 space-y-1">
                        <li>1. Build a portfolio project that solves a real problem...</li>
                        <li>2. Practice the STAR method for behavioral questions...</li>
                        <li>3. Research the company&apos;s tech stack and prepare...</li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div className="absolute -bottom-4 -right-4 bg-[#A855F7] text-white rounded-xl px-4 py-2 font-bold shadow-lg">
                  Live AI Responses
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Features Section */}
      <section className="py-24 sm:py-32 bg-[#0A1F44]/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#A855F7] mb-4 block">
              Interactive Learning
            </span>
            <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-white mb-6">
              Learn by Doing
            </h2>
            <p className="text-lg text-[#94A3B8] leading-relaxed">
              This isn&apos;t just theory—you&apos;ll practice with real AI tools and get instant feedback on your prompts.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div 
                key={`feature-${index}`}
                className="bg-[#0A1F44]/50 backdrop-blur-md border border-[#A855F7]/20 rounded-2xl p-6 hover:border-[#A855F7]/50 transition-all duration-300"
              >
                <div className="w-14 h-14 rounded-2xl bg-[#A855F7]/20 flex items-center justify-center mb-4">
                  <feature.icon className="w-7 h-7 text-[#A855F7]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-[#94A3B8] text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Curriculum Section */}
      <section className="py-24 sm:py-32 bg-[#060F22]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#A855F7] mb-4 block">
              Course Curriculum
            </span>
            <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-white mb-6">
              6 Modules to Mastery
            </h2>
            <p className="text-lg text-[#94A3B8] leading-relaxed">
              A structured journey from AI basics to advanced prompt engineering techniques.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {modules.map((module) => (
              <div 
                key={`module-${module.number}`}
                className="bg-[#0A1F44]/50 backdrop-blur-md border border-[#00AEEF]/20 rounded-2xl p-6 hover:border-[#A855F7]/50 transition-all duration-300 group"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#A855F7] to-[#EC4899] flex items-center justify-center font-bold text-white text-lg">
                    {module.number}
                  </div>
                  <div>
                    <div className="flex items-center gap-2 text-[#94A3B8] text-sm">
                      <Clock className="w-4 h-4" />
                      <span>{module.duration}</span>
                      <span className="text-[#A855F7]">•</span>
                      <span>{module.lessons} lessons</span>
                    </div>
                  </div>
                </div>
                
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-[#A855F7] transition-colors">{module.title}</h3>
                <p className="text-[#94A3B8] text-sm mb-4">{module.description}</p>
                
                <ul className="space-y-2">
                  {module.topics.map((topic, i) => (
                    <li key={`topic-${module.number}-${i}`} className="flex items-center gap-2 text-[#94A3B8] text-sm">
                      <CheckCircle className="w-4 h-4 text-[#00FFE0] flex-shrink-0" />
                      {topic}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Badges Section */}
      <section className="py-24 sm:py-32 bg-[#0A1F44]/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#A855F7] mb-4 block">
              Earn Achievements
            </span>
            <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-white mb-6">
              Collect Badges as You Learn
            </h2>
            <p className="text-lg text-[#94A3B8] leading-relaxed">
              Track your progress and showcase your achievements with shareable digital badges.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {badges.map((badge, index) => (
              <div 
                key={`badge-${index}`}
                className="bg-[#0A1F44]/50 backdrop-blur-md border border-[#00AEEF]/20 rounded-2xl p-6 text-center hover:border-[#A855F7]/50 transition-all duration-300 group"
              >
                <div 
                  className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
                  style={{ backgroundColor: `${badge.color}20` }}
                >
                  <badge.icon className="w-10 h-10" style={{ color: badge.color }} />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{badge.name}</h3>
                <p className="text-[#94A3B8] text-sm">{badge.requirement}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Templates Preview */}
      <section className="py-24 sm:py-32 bg-[#060F22]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#A855F7] mb-4 block">
              Ready-to-Use Templates
            </span>
            <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-white mb-6">
              Start Creating Immediately
            </h2>
            <p className="text-lg text-[#94A3B8] leading-relaxed">
              Get access to 6 professional prompt templates you can use right away.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {promptTemplates.map((template, index) => (
              <div 
                key={`template-${index}`}
                className="bg-[#0A1F44]/50 backdrop-blur-md border border-[#00AEEF]/20 rounded-2xl p-6 hover:border-[#A855F7]/50 transition-all duration-300 group cursor-pointer"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#A855F7]/20 flex items-center justify-center flex-shrink-0">
                    <Zap className="w-6 h-6 text-[#A855F7]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white mb-1 group-hover:text-[#A855F7] transition-colors">{template.name}</h3>
                    <p className="text-[#94A3B8] text-sm">{template.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 sm:py-32 bg-gradient-to-b from-[#0A1F44]/30 to-[#060F22]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <div className="bg-gradient-to-r from-[#A855F7]/20 to-[#EC4899]/20 border border-[#A855F7]/30 rounded-3xl p-12">
            <Brain className="w-16 h-16 text-[#A855F7] mx-auto mb-6" />
            <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-white mb-6">
              Ready to Master AI?
            </h2>
            <p className="text-lg text-[#94A3B8] leading-relaxed mb-8 max-w-2xl mx-auto">
              Join thousands of students who are already using AI to boost their productivity, accelerate their careers, and unlock new possibilities.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/enrollment"
                data-testid="ai-program-cta-enroll-btn"
                className="inline-flex items-center justify-center bg-gradient-to-r from-[#A855F7] to-[#EC4899] text-white font-bold rounded-xl px-8 py-4 shadow-[0_0_30px_rgba(168,85,247,0.4)] hover:shadow-[0_0_40px_rgba(168,85,247,0.6)] hover:-translate-y-1 transition-all duration-300 gap-2"
              >
                Enroll Now — It&apos;s Free
                <ArrowUpRight className="w-5 h-5" />
              </Link>
              <Link
                to="/programs"
                data-testid="ai-program-view-all-btn"
                className="inline-flex items-center justify-center bg-transparent border-2 border-white/30 text-white font-bold rounded-xl px-8 py-4 hover:bg-white/10 transition-all duration-300 gap-2"
              >
                View All Programs
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AIPrograms;
